import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pain-point focused quiz.
 * Each option carries `points` mapping service_key -> integer.
 * Services: carebnb, workshops, energy, coaching, courses
 */
const QUESTIONS = [
  {
    id: 'audience',
    prompt: "First — who is this for?",
    caption: "Helps me tailor your best-fit path.",
    options: [
      { label: "I'm a mom — and I'm looking for support for myself.", points: {} },
      { label: "I'm not a mom yet — engaged, newly married, or preparing for that life.", points: { workshops: 1, courses: 1 } },
      { label: "I'm exploring this on behalf of my wife, daughter, or a friend.", points: { carebnb: 1 } },
      { label: "I'm not a mom — I'm curious about the work for myself.", points: { energy: 1, coaching: 1 } },
    ],
  },
  {
    id: 'pain',
    prompt: "What feels heaviest right now?",
    caption: "Pick the one that hits closest.",
    options: [
      { label: "I feel lost in my kitchen. I want to make tasty, simple, healthy food but I don't know where to start.", points: { coaching: 3, courses: 2, carebnb: 1 } },
      { label: "I've gained weight I don't want — and I don't know how to eat differently without dieting.", points: { coaching: 3, courses: 1 } },
      { label: "New dietary restrictions were dropped on me. I'm stuck trying to figure out what to make.", points: { coaching: 3, courses: 2 } },
      { label: "Shabbos is coming — again — and I dread the food. I don't know how to make it feel nourishing.", points: { carebnb: 3, courses: 2, workshops: 2 } },
      { label: "I'm carrying invisible weight. I feel disconnected from myself. Something has to shift.", points: { energy: 3, carebnb: 2 } },
      { label: "I just need to get away. Be taught. Be poured back into. Come home restored.", points: { carebnb: 3, workshops: 1 } },
    ],
  },
  {
    id: 'bandwidth',
    prompt: "How much space do you have for support right now?",
    caption: "Be honest — the right fit depends on it.",
    options: [
      { label: "Basically none. I need something that works while I live my life.", points: { energy: 3 } },
      { label: "A weekend, once. Let me get away, learn, come home changed.", points: { carebnb: 3 } },
      { label: "1:1 conversations I can show up to when we schedule them.", points: { coaching: 3, energy: 1 } },
      { label: "I want to learn at my own pace, in my own kitchen, on my own time.", points: { courses: 3 } },
      { label: "I want to be with other women, learning together in a group.", points: { workshops: 3, carebnb: 1 } },
    ],
  },
  {
    id: 'outcome',
    prompt: "One year from now — what would you love to be true?",
    caption: "The vision that makes you exhale.",
    options: [
      { label: "I cook with confidence. Colorful food. No stress. My family notices.", points: { coaching: 2, courses: 2, carebnb: 1 } },
      { label: "I feel like myself again. Grounded. Present. Whole.", points: { energy: 3, carebnb: 2 } },
      { label: "Shabbos is a joy — for me and my family. I love my kitchen.", points: { carebnb: 3, courses: 1 } },
      { label: "I have real ongoing support and a community of women beside me.", points: { workshops: 3, coaching: 1 } },
      { label: "I've moved through emotional weight I've been carrying for years.", points: { energy: 3, coaching: 1 } },
    ],
  },
];

const SERVICE_MAP = {
  carebnb: {
    name: "CareBnB Shabbat Chavaya",
    tagline: "You need to get away — and be taught while you rest.",
    description: "A private BnB reset in Efrat, Israel. Thursday to Motzei Shabbos of cooking with Chanah, learning to make Shabbos, a Circle of Life coaching session, and Shabbat with Rabbi Shlomo Katz's community. You come home rooted.",
    cta: "Explore Shabbat Chavaya",
    href: "/carebnb",
  },
  workshops: {
    name: "Momshiach Workshops",
    tagline: "You want to learn with other women — hands on.",
    description: "Live, in-person gatherings for women learning how to nourish themselves. Community, cooking, real skills — in Israel and online.",
    cta: "WhatsApp Chanah",
    external: "https://wa.me/972529326458?text=Hi%20Chanah%2C%20I'd%20love%20to%20learn%20about%20your%20upcoming%20Momshiach%20Workshops",
  },
  energy: {
    name: "Energy Therapy",
    tagline: "You need something passive that meets you while you live your life.",
    description: "Kosher, remote energy work that clears blockages while you go about your day. No time commitment, no Zoom calls. You just receive.",
    cta: "Start Your Journey",
    href: "/",
    scrollAfter: "hero-start-journey-btn",
  },
  coaching: {
    name: "Health Coaching",
    tagline: "You need 1:1 support to figure out food — without judgment.",
    description: "A coaching arc built on the principle: we don't remove, we add color. Confidence in your kitchen, colorful food on your plate, and a plan that fits your real life.",
    cta: "Explore Coaching",
    href: "/health-coaching",
  },
  courses: {
    name: "Online Courses & Resources",
    tagline: "You want tools you can use on your own time.",
    description: "Fridge Magic, Cook the Book club, digital workbooks, and a free 7-day challenge. Learn in your own kitchen, at your own pace.",
    cta: "Browse Resources",
    href: "/resources",
  },
};

const EcosystemQuiz = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const q = QUESTIONS[step];

  const pick = (optIdx) => {
    const newAnswers = { ...answers, [q.id]: optIdx };
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      setShowResult(true);
    }
  };

  const scores = React.useMemo(() => {
    const totals = { carebnb: 0, workshops: 0, energy: 0, coaching: 0, courses: 0 };
    Object.entries(answers).forEach(([qid, optIdx]) => {
      const question = QUESTIONS.find((qq) => qq.id === qid);
      if (!question) return;
      const opt = question.options[optIdx];
      Object.entries(opt.points).forEach(([svc, pts]) => {
        totals[svc] += pts;
      });
    });
    return totals;
  }, [answers]);

  const topService = React.useMemo(() => {
    let best = null;
    let bestScore = -1;
    Object.entries(scores).forEach(([svc, s]) => {
      if (s > bestScore) { bestScore = s; best = svc; }
    });
    return best;
  }, [scores]);

  const runnersUp = React.useMemo(() => {
    return Object.entries(scores)
      .filter(([k]) => k !== topService)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .filter(([, s]) => s > 0)
      .map(([k]) => k);
  }, [scores, topService]);

  const restart = () => { setStep(0); setAnswers({}); setShowResult(false); };

  // Log completion to backend analytics (fires once when result screen opens)
  useEffect(() => {
    if (!showResult || !topService) return;
    axios.post(`${API}/quiz-completions`, {
      audience_idx: answers['audience'],
      top_service: topService,
      runners_up: runnersUp,
      scores,
      answers,
    }).catch(() => { /* silent */ });
  }, [showResult]);

  // Personalized intro copy based on the audience question (question 0)
  const audienceIdx = answers['audience'];
  const AUDIENCE_INTROS = [
    { intro: "As a mom looking for support for yourself,", secondary: null },
    { intro: "As someone preparing for the next chapter of life,", secondary: null },
    {
      intro: "Since you're exploring this for someone you love,",
      secondary: {
        label: "You might also consider gifting a getaway",
        href: "/gift",
      },
    },
    { intro: "Since you're curious about the work for yourself,", secondary: null },
  ];
  const audienceCopy = AUDIENCE_INTROS[audienceIdx] || AUDIENCE_INTROS[0];

  return (
    <div className="quiz-overlay" data-testid="ecosystem-quiz">
      <div className="quiz-container">
        <button onClick={onClose} className="quiz-close" data-testid="quiz-close">
          <X size={22} />
        </button>

        {!showResult ? (
          <>
            <div className="quiz-progress">
              {QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`quiz-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
                />
              ))}
            </div>

            <div className="quiz-question" data-testid={`quiz-q-${step}`}>
              <p className="quiz-step-label">Question {step + 1} of {QUESTIONS.length}</p>
              <h2 className="heading-1">{q.prompt}</h2>
              <p className="body-medium text-secondary">{q.caption}</p>

              <div className="quiz-options">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => pick(i)}
                    className={`quiz-option ${answers[q.id] === i ? 'active' : ''}`}
                    data-testid={`quiz-opt-${step}-${i}`}
                  >
                    <span className="quiz-option-num">{String.fromCharCode(65 + i)}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="quiz-result" data-testid="quiz-result">
            <div className="quiz-eyebrow">
              <Sparkles size={14} />
              <span>Your best-fit path</span>
            </div>
            <p className="body-medium quiz-audience-intro" data-testid="quiz-audience-intro">
              {audienceCopy.intro} here&apos;s what fits you best:
            </p>
            <h2 className="heading-1" data-testid="quiz-result-title">{SERVICE_MAP[topService].name}</h2>
            <p className="body-large quiz-tagline">{SERVICE_MAP[topService].tagline}</p>
            <p className="body-medium quiz-desc">{SERVICE_MAP[topService].description}</p>

            {SERVICE_MAP[topService].external ? (
              <a
                href={SERVICE_MAP[topService].external}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-lg"
                data-testid="quiz-primary-cta"
                onClick={onClose}
              >
                <span>{SERVICE_MAP[topService].cta}</span>
                <ArrowRight size={18} />
              </a>
            ) : (
              <Link
                to={SERVICE_MAP[topService].href}
                className="btn-primary btn-lg"
                data-testid="quiz-primary-cta"
                onClick={onClose}
              >
                <span>{SERVICE_MAP[topService].cta}</span>
                <ArrowRight size={18} />
              </Link>
            )}

            {audienceCopy.secondary && (
              <Link
                to={audienceCopy.secondary.href}
                className="quiz-audience-secondary"
                onClick={onClose}
                data-testid="quiz-audience-secondary"
              >
                <Sparkles size={14} />
                <span>{audienceCopy.secondary.label}</span>
                <ArrowRight size={14} />
              </Link>
            )}

            {runnersUp.length > 0 && (
              <div className="quiz-runners">
                <p className="caption">Also worth exploring:</p>
                <div className="quiz-runners-grid">
                  {runnersUp.map((svc) => {
                    const s = SERVICE_MAP[svc];
                    return s.external ? (
                      <a
                        key={svc}
                        href={s.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="quiz-runner-card"
                        onClick={onClose}
                      >
                        <strong>{s.name}</strong>
                        <span>{s.tagline}</span>
                      </a>
                    ) : (
                      <Link key={svc} to={s.href} className="quiz-runner-card" onClick={onClose}>
                        <strong>{s.name}</strong>
                        <span>{s.tagline}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            <button onClick={restart} className="btn-secondary" data-testid="quiz-restart">
              Take the quiz again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EcosystemQuiz;
